import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight,
  MapPin,
  Truck,
  ChevronDown,
  Calculator,
  DollarSign,
  RotateCcw,
  Radio,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const equipmentTypes = [
  'Dry Van',
  'Flatbed',
  'Reefer',
  'Step Deck',
  'Container',
  'Power Only',
];

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

// Live ticker dataset — refreshed from /api/quote/lanes/top if available, else fallback
const FALLBACK_TICKER = [
  { from: 'CA', to: 'TX', equip: 'Dry Van', mins: 3 },
  { from: 'IL', to: 'FL', equip: 'Reefer', mins: 8 },
  { from: 'TX', to: 'GA', equip: 'Flatbed', mins: 12 },
  { from: 'NY', to: 'NC', equip: 'Dry Van', mins: 17 },
  { from: 'WA', to: 'CO', equip: 'Container', mins: 22 },
  { from: 'OH', to: 'TN', equip: 'Dry Van', mins: 28 },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

export const HeroSection = ({ stats }) => {
  const [originState, setOriginState] = useState('');
  const [destinationState, setDestinationState] = useState('');
  const [equipment, setEquipment] = useState('');
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [tickerData, setTickerData] = useState(FALLBACK_TICKER);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const audioCtxRef = useRef(null);
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const bgRef = useRef(null);

  // ---------- Synth ping (Web Audio API) when ticker rotates, if user opted in ----------
  const playPing = useCallback(() => {
    if (!soundOn) return;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1320, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.16);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.35);
    } catch {
      /* ignore audio failures */
    }
  }, [soundOn]);

  // ---------- Mouse parallax on the truck background ----------
  useEffect(() => {
    const section = sectionRef.current;
    const bg = bgRef.current;
    if (!section || !bg) return;
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = null;
    const handleMove = (e) => {
      const rect = section.getBoundingClientRect();
      const cx = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
      const cy = (e.clientY - rect.top) / rect.height - 0.5;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        bg.style.transform = `scale(1.08) translate3d(${cx * -24}px, ${cy * -16}px, 0)`;
      });
    };
    const handleLeave = () => {
      if (rafId) cancelAnimationFrame(rafId);
      bg.style.transform = '';
    };
    section.addEventListener('mousemove', handleMove);
    section.addEventListener('mouseleave', handleLeave);
    return () => {
      section.removeEventListener('mousemove', handleMove);
      section.removeEventListener('mouseleave', handleLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // ---------- Fetch top lanes for the live ticker ----------
  useEffect(() => {
    axios
      .get(`${API}/quote/lanes/top/public?limit=8`)
      .then((res) => {
        const lanes = res.data?.lanes || [];
        const cleaned = lanes
          .map((l, i) => {
            const equip = (l.equipment_type || '').trim();
            // Only keep lanes with equipment types that exist in our dropdown
            if (!equipmentTypes.includes(equip)) return null;
            return {
              from: l.origin_state,
              to: l.destination_state,
              equip,
              mins: 2 + i * 4 + Math.floor(Math.random() * 4),
            };
          })
          .filter(Boolean);
        if (cleaned.length > 0) setTickerData(cleaned);
      })
      .catch(() => {
        /* keep fallback */
      });
  }, []);

  // ---------- Rotate the ticker every 3.5s ----------
  useEffect(() => {
    const t = setInterval(() => {
      setTickerIdx((i) => (i + 1) % tickerData.length);
      playPing();
    }, 3500);
    return () => clearInterval(t);
  }, [tickerData.length, playPing]);

  const handleCalculate = useCallback(async (e) => {
    e.preventDefault();
    if (!originState || !destinationState || !equipment) {
      toast.error('Please select origin, destination and equipment.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quote/estimate`, {
        origin_state: originState,
        origin_city: '',
        destination_state: destinationState,
        destination_city: '',
        equipment_type: equipment,
      });
      setEstimate(res.data);
    } catch {
      toast.error('Could not calculate right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [originState, destinationState, equipment]);

  const handleReset = () => setEstimate(null);

  const handleDetailed = () => {
    const params = new URLSearchParams({
      origin: originState,
      destination: destinationState,
      equipment,
    });
    navigate(`/quote?${params.toString()}`);
  };

  const currentTick = tickerData[tickerIdx];

  const loadLaneFromTicker = () => {
    setOriginState(currentTick.from);
    setDestinationState(currentTick.to);
    setEquipment(currentTick.equip);
    setEstimate(null);
    const el = document.querySelector('[data-testid=hero-quote-calculator]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('hero-calc-flash');
      setTimeout(() => el.classList.remove('hero-calc-flash'), 1200);
    }
    toast.success(`Lane loaded: ${currentTick.from} → ${currentTick.to} · ${currentTick.equip}`);
  };

  // Reusable ticker — rendered above-the-fold on both desktop (right column top) and mobile (between headline and calculator)
  const renderTicker = (variant = 'desktop') => {
    const isCompact = variant === 'mobile';
    return (
      <div className={`relative ${isCompact ? 'mb-6' : ''} w-full max-w-md ${isCompact ? '' : 'ml-auto'}`}>
        <motion.button
          type="button"
          onClick={loadLaneFromTicker}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: isCompact ? 0.5 : 0.4 }}
          className={`group bg-zinc-950/90 backdrop-blur-md border border-zinc-700 hover:border-red-600 ${isCompact ? 'px-4 py-3' : 'px-5 py-4'} w-full flex items-center gap-3 sm:gap-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:bg-zinc-900/90 cursor-pointer`}
          data-testid={isCompact ? 'hero-live-ticker-mobile' : 'hero-live-ticker'}
          aria-label="Click to load this lane into the calculator"
        >
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            <Radio className="w-3.5 h-3.5 text-red-500" />
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-red-400 hidden sm:inline">
              Live Match
            </span>
          </div>
          <div className="h-6 w-px bg-zinc-700 flex-shrink-0" />
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentTick.from}-${currentTick.to}-${tickerIdx}-${variant}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2.5 flex-1 min-w-0"
            >
              <span className="text-base font-black text-white tabular-nums">
                {currentTick.from}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
              <span className="text-base font-black text-white tabular-nums">
                {currentTick.to}
              </span>
              <span className="text-zinc-600 text-xs hidden sm:inline">·</span>
              <span className="text-xs text-zinc-300 font-medium truncate hidden sm:inline">
                {currentTick.equip}
              </span>
              <span className="text-zinc-600 text-xs ml-auto flex-shrink-0">
                {currentTick.mins}m
              </span>
            </motion.div>
          </AnimatePresence>
          <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </motion.button>
        {/* Sound toggle — only on desktop variant to keep mobile minimal */}
        {!isCompact && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSoundOn((s) => !s);
              if (!soundOn) setTimeout(playPing, 80);
            }}
            className="absolute -top-3 -right-3 w-7 h-7 bg-zinc-900 border border-zinc-700 hover:border-red-600 flex items-center justify-center transition-colors text-zinc-400 hover:text-red-500"
            data-testid="hero-ticker-sound-toggle"
            aria-label={soundOn ? 'Mute live ticker sound' : 'Unmute live ticker sound'}
            title={soundOn ? 'Mute' : 'Sound on'}
          >
            {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex items-center overflow-hidden hero-section min-h-[640px]"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Background image with mouse parallax + Ken Burns slow zoom */}
      <div className="absolute inset-0 bg-zinc-950" />
      <div
        ref={bgRef}
        className="hero-bg absolute inset-0"
        style={{
          backgroundImage: `url('/hero-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          willChange: 'transform',
          transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />

      {/* Horizontal dark gradient overlay so left copy stays crisp */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to right, rgba(9,9,11,0.96) 0%, rgba(9,9,11,0.70) 35%, rgba(9,9,11,0.20) 65%, rgba(9,9,11,0.50) 100%)',
        }}
      />

      {/* Animated horizontal red light streaks ("passing taillights") */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span className="hero-streak hero-streak-1" />
        <span className="hero-streak hero-streak-2" />
        <span className="hero-streak hero-streak-3" />
      </div>

      {/* Grid lines overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 120px), repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 120px)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 lg:py-12 xl:py-16">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* LEFT: Copy + Calculator */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {/* Pulsing LIVE NETWORK badge */}
              <div className="inline-flex items-center gap-2 mb-4 bg-red-600/15 border border-red-600/40 px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <span className="text-red-400 text-[10px] font-bold uppercase tracking-[0.25em]">
                  Network Live · 48 States
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-black text-white leading-[0.95] tracking-tighter mb-4">
                COMMAND YOUR<br />
                <span className="text-red-500">FREIGHT.</span>
              </h1>
              <p
                className="text-zinc-400 text-base xl:text-lg leading-relaxed mb-5 max-w-md"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                High-authority asset-based carrier powering FTL, LTL, Intermodal & Drayage
                across 48 states with our own fleet + 2,500+ partner trucks.
              </p>
            </motion.div>

            {/* Mobile live ticker — visible above the fold on small screens */}
            <div className="lg:hidden">
              {renderTicker('mobile')}
            </div>

            {/* Mini Quote Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="bg-zinc-900/70 backdrop-blur-xl border border-zinc-800 p-5 sm:p-6"
              data-testid="hero-quote-calculator"
            >
              <AnimatePresence mode="wait" initial={false}>
                {!estimate ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Calculator className="w-4 h-4 text-red-500" />
                      <h3
                        className="text-white font-bold text-base"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Instant Rate Calculator
                      </h3>
                    </div>
                    <p
                      className="text-zinc-500 text-xs mb-4"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Live market estimate. No email required.
                    </p>
                    <form onSubmit={handleCalculate} className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 z-10 pointer-events-none" />
                          <Select value={originState} onValueChange={setOriginState}>
                            <SelectTrigger
                              className="w-full bg-zinc-950/70 border-zinc-800 text-white pl-10 pr-3 py-2.5 text-sm h-auto focus:outline-none focus:ring-0 focus:border-red-600 hover:border-zinc-700 transition-colors data-[placeholder]:text-zinc-500"
                              data-testid="hero-origin-state"
                            >
                              <SelectValue placeholder="Origin state" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-72">
                              {US_STATES.map(([code, name]) => (
                                <SelectItem key={code} value={code} className="text-white focus:bg-zinc-800 focus:text-white">
                                  {`${name} (${code})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 z-10 pointer-events-none" />
                          <Select value={destinationState} onValueChange={setDestinationState}>
                            <SelectTrigger
                              className="w-full bg-zinc-950/70 border-zinc-800 text-white pl-10 pr-3 py-2.5 text-sm h-auto focus:outline-none focus:ring-0 focus:border-red-600 hover:border-zinc-700 transition-colors data-[placeholder]:text-zinc-500"
                              data-testid="hero-destination-state"
                            >
                              <SelectValue placeholder="Destination state" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-72">
                              {US_STATES.map(([code, name]) => (
                                <SelectItem key={code} value={code} className="text-white focus:bg-zinc-800 focus:text-white">
                                  {`${name} (${code})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="relative">
                        <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 z-10 pointer-events-none" />
                        <Select value={equipment} onValueChange={setEquipment}>
                          <SelectTrigger
                            className="w-full bg-zinc-950/70 border-zinc-800 text-white pl-10 pr-3 py-2.5 text-sm h-auto focus:outline-none focus:ring-0 focus:border-red-600 hover:border-zinc-700 transition-colors data-[placeholder]:text-zinc-500"
                            data-testid="hero-equipment-select"
                          >
                            <SelectValue placeholder="Equipment type" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 text-white max-h-72">
                            {equipmentTypes.map((t) => (
                              <SelectItem key={t} value={t} className="text-white focus:bg-zinc-800 focus:text-white">
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
                        data-testid="hero-calculate-button"
                      >
                        {loading ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            Calculating…
                          </>
                        ) : (
                          <>
                            Calculate Estimate
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </form>
                    <p
                      className="text-zinc-600 text-xs mt-3 text-center"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Or call{' '}
                      <a href="tel:+18776809272" className="text-red-500 hover:text-red-400">
                        1 (877) 680-9272
                      </a>{' '}
                      for a live agent
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    data-testid="hero-quote-result"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-red-500" />
                        <h3
                          className="text-white font-bold text-lg"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          Your Estimate
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="text-zinc-500 hover:text-white text-xs uppercase tracking-wider flex items-center gap-1 transition-colors"
                        data-testid="hero-quote-reset"
                      >
                        <RotateCcw className="w-3 h-3" />
                        New
                      </button>
                    </div>
                    <p
                      className="text-zinc-500 text-xs mb-5"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {estimate.origin_location} → {estimate.destination_location} ·{' '}
                      {estimate.equipment_type} · ~{estimate.estimated_miles} mi
                    </p>

                    <div className="grid grid-cols-3 gap-2 mb-5">
                      <div className="bg-zinc-950/60 border border-zinc-800 px-3 py-4 text-center">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">
                          Low
                        </div>
                        <div
                          className="text-xl font-black text-white"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                          data-testid="hero-quote-low"
                        >
                          {formatCurrency(estimate.estimate_low)}
                        </div>
                      </div>
                      <div className="bg-red-600/10 border border-red-600/60 px-3 py-4 text-center -my-1">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1">
                          Typical
                        </div>
                        <div
                          className="text-2xl font-black text-white"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                          data-testid="hero-quote-mid"
                        >
                          {formatCurrency(estimate.estimate_mid)}
                        </div>
                      </div>
                      <div className="bg-zinc-950/60 border border-zinc-800 px-3 py-4 text-center">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">
                          High
                        </div>
                        <div
                          className="text-xl font-black text-white"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                          data-testid="hero-quote-high"
                        >
                          {formatCurrency(estimate.estimate_high)}
                        </div>
                      </div>
                    </div>

                    <p
                      className="text-zinc-500 text-[11px] leading-relaxed mb-5"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      Estimate is based on historical lane rates. {estimate.fuel_surcharge_note}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={handleDetailed}
                        className="bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
                        data-testid="hero-get-detailed-quote"
                      >
                        Get Detailed Quote
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                      <Link
                        to="/quote-calculator"
                        className="border border-zinc-700 hover:border-white text-white py-3 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
                        data-testid="hero-refine-quote"
                      >
                        Refine With City
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* RIGHT: Live ticker + Stats (desktop) */}
          <div className="lg:col-span-7 hidden lg:flex flex-col items-end justify-center gap-5">
            {/* Live ticker — placed FIRST so it's always above the fold */}
            {renderTicker('desktop')}

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="text-right"
            >
              <div className="grid grid-cols-2 gap-4 max-w-md ml-auto">
                {[
                  { value: stats?.available_loads || '50', suffix: '+', label: 'ACTIVE LOADS' },
                  { value: stats?.states_covered || '48', suffix: '', label: 'STATES COVERED' },
                  { value: '2,500', suffix: '+', label: 'PARTNER TRUCKS' },
                  { value: '99.8', suffix: '%', label: 'ON-TIME RATE' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="hero-stat bg-zinc-900/40 backdrop-blur border border-zinc-800 p-5 transition-all duration-300 hover:border-red-600/60 hover:-translate-y-0.5"
                  >
                    <div
                      className="text-3xl font-black text-white tracking-tight"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {stat.value}
                      <span className="text-red-500">{stat.suffix}</span>
                    </div>
                    <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile stats */}
      <div className="absolute bottom-0 left-0 right-0 bg-zinc-950/90 backdrop-blur-md border-t border-zinc-800 lg:hidden">
        <div className="grid grid-cols-4 divide-x divide-zinc-800">
          {[
            { v: stats?.available_loads || '50', s: '+', l: 'Loads' },
            { v: '48', s: '', l: 'States' },
            { v: '2.5K', s: '+', l: 'Carriers' },
            { v: '24/7', s: '', l: 'Support' },
          ].map((s) => (
            <div key={s.l} className="text-center py-4">
              <div className="text-lg font-black text-white">
                {s.v}
                <span className="text-red-500">{s.s}</span>
              </div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-wider">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
